const API_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Dog API Service
 * Handles all dog-related API operations
 */
export class DogService {
    
    /**
     * Fetch all dogs
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Array>} Array of dogs
     */
    static async getAllDogs(authorizedFetch, params = {}) {
        const qs = new URLSearchParams(
          Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
        ).toString();
        const response = await authorizedFetch(`${API_URL}/api/dogs${qs ? `?${qs}` : ''}`);
        if (!response.ok) {
            throw new Error(await response.text());
        }
        return response.json();
    }

    /**
     * Create a new dog
     * @param {FormData|Object} dogData - Dog data to create (FormData for image uploads, Object for JSON)
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Created dog
     */
    static async createDog(dogData, authorizedFetch) {
        const isFormData = dogData instanceof FormData;
        
        const requestOptions = {
            method: 'POST',
            body: isFormData ? dogData : JSON.stringify(dogData)
        };
        
        // Only set Content-Type for JSON, let browser set it for FormData
        if (!isFormData) {
            requestOptions.headers = { 'Content-Type': 'application/json' };
        }
        
        const response = await authorizedFetch(`${API_URL}/api/dogs`, requestOptions);
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText);
        }
        
        return response.json();
    }

    /**
     * Update an existing dog
     * @param {string} dogId - ID of dog to update
     * @param {FormData|Object} dogData - Updated dog data (FormData for image uploads, Object for JSON)
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Updated dog
     */
    static async updateDog(dogId, dogData, authorizedFetch) {
        const isFormData = dogData instanceof FormData;
        
        const requestOptions = {
            method: 'PUT',
            body: isFormData ? dogData : JSON.stringify(dogData)
        };
        
        // Only set Content-Type for JSON, let browser set it for FormData
        if (!isFormData) {
            requestOptions.headers = { 'Content-Type': 'application/json' };
        }
        
        const response = await authorizedFetch(`${API_URL}/api/dogs/${dogId}`, requestOptions);
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText);
        }
        
        return response.json();
    }

    /**
     * Delete a dog
     * @param {string} dogId - ID of dog to delete
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<void>}
     */
    static async deleteDog(dogId, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/dogs/${dogId}`, {
            method: 'DELETE'
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
    }
}
